import { entityToOption, type ReferenceOption, referenceToOption } from '../../form/forms';
import { resolveOptionLabel } from './autocompleteLabelUtils';
import { resolveOptionIdentity as resolveAutocompleteOptionIdentity } from './autocompleteOptionIdentityUtils';

export const getValue = (multiple, value, notOnlyRef, returnObjectAsValue, key) => {
    const resolveEntityReference = (item) => {
        return item?.[key] || item?.reference || item?.personReference;
    };

    const getValueItem = (item) => {
        if (returnObjectAsValue) {
            if (item == null) {
                return entityToOption(undefined, undefined);
            }
            if (typeof item !== 'object') {
                const stringValue = String(item);
                return {
                    id: stringValue,
                    label: stringValue
                } as ReferenceOption;
            }

            return entityToOption(item, resolveEntityReference(item));
        }

        if (!notOnlyRef) {
            const referenceValue = resolveEntityReference(item) || item;
            const option = referenceToOption(referenceValue);
            if (!option.label) {
                option.label = resolveOptionLabel(item);
            }
            return option;
        }

        if (item && typeof item === 'object' && !item.label) {
            return {
                ...item,
                label: resolveOptionLabel(item)
            };
        }

        return item;
    };

    if (multiple) {
        if (value === undefined || value === null) {
            return [];
        }
        if (Array.isArray(value)) {
            return value.map((item) => getValueItem(item));
        }
    } else {
        if (value == null) return '';
        return getValueItem(value);
    }
};

export const setDefaultValueById = (
    defaultValueIds: string[],
    multiple: boolean,
    options: ReferenceOption[],
    handleChange: (value) => void
) => {
    if (multiple) {
        const filteredOptions = options.filter((option) => defaultValueIds.includes(option.id));
        handleChange(filteredOptions);
    } else {
        const findValue = options.find((option) => defaultValueIds.includes(option.id));
        handleChange(findValue);
    }
};

export const generateCreateOption = (searchTerm: string, entityReferenceKey: string) => {
    return {
        id: '',
        label: searchTerm,
        [entityReferenceKey]: {
            id: '',
            label: searchTerm
        },
        createOption: true
    };
};

type DeriveCreateSearchTermArgs = {
    effectiveInputValue: string;
    multiple: boolean;
    addOption: boolean;
    isAIExtractedData: boolean;
    value: any;
    allowFreeForm: boolean;
    skipReferenceCreationOnNoMatch: boolean;
};

type DeriveFilterInputValueArgs = {
    userInputValue: string;
    multiple: boolean;
    selectedValue: ReferenceOption | string | undefined;
};

type DeriveUserInputValueArgs = {
    typedInputValue: string;
    multiple: boolean;
    editingChipKey: string | null;
    editingChipSearchTerm: string;
};

export const deriveUserInputValue = ({
    typedInputValue,
    multiple,
    editingChipKey,
    editingChipSearchTerm
}: DeriveUserInputValueArgs) => {
    const chipEditingInputValue =
        multiple && editingChipKey && editingChipSearchTerm ? editingChipSearchTerm : '';

    return typedInputValue || chipEditingInputValue;
};

export const deriveFilterInputValue = ({
    userInputValue,
    multiple,
    selectedValue
}: DeriveFilterInputValueArgs) => {
    const selectedValueLabel =
        !userInputValue && !multiple
            ? typeof selectedValue === 'string'
                ? selectedValue
                : selectedValue && typeof selectedValue === 'object'
                  ? resolveOptionLabel(selectedValue)
                  : ''
            : '';

    return userInputValue || selectedValueLabel;
};

export const deriveCreateSearchTerm = ({
    effectiveInputValue,
    multiple,
    addOption,
    isAIExtractedData,
    value,
    allowFreeForm,
    skipReferenceCreationOnNoMatch
}: DeriveCreateSearchTermArgs) => {
    let searchTerm = effectiveInputValue;

    if (!multiple && addOption && isAIExtractedData && !effectiveInputValue) {
        const aiSelectedLabel =
            value && typeof value === 'object'
                ? resolveOptionLabel(value as ReferenceOption)
                : typeof value === 'string'
                  ? value
                  : '';

        if (aiSelectedLabel) {
            searchTerm = aiSelectedLabel;
        }
    }
    if (
        allowFreeForm &&
        skipReferenceCreationOnNoMatch &&
        !value?.ref?.id &&
        !effectiveInputValue &&
        !!value?.label
    ) {
        searchTerm = value.label;
    }

    return searchTerm;
};

export const hasExactLabelMatch = (options: ReferenceOption[], searchTerm: string) => {
    if (!searchTerm) {
        return false;
    }
    return options.some((option) => searchTerm === resolveOptionLabel(option));
};

export const resolveOptionIdentity = (option: ReferenceOption) => {
    return resolveAutocompleteOptionIdentity(option);
};
